import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { Grid } from '@mantine/core';
import DeleteButton from '@/components/DeleteButton';
import { labEntryActions, projectEntryActions } from '../../store';
import { handleFetchError, isTimeEntryDateValid } from '@/helpers';
import { SupportedSystems } from '@/shared';
import { GrsEnvironmentUrl } from '@/helpers/environmentHelpers';
import NewHourInput from '../../components/ProjectEntrySection/HoursInput/NewHourInput';
import FillWeekControl from '@/components/FillWeekControl';
import { errorSavingEntry, errorSavingEntryHours } from '../../shared/constants/toastrErrorMsg';
import { ProjectEntryRow } from '../../components/ProjectEntrySection/RowContent/styled';
import { HeaderRow, StyledArrowIcon } from './styled';

const LabSection = (props) => {
  const dispatch = useDispatch();
  const userId = useSelector(state => state.user.activeUser?.oid);
  const selectedDays = useSelector(state => state.projectEntry.projectEntries.selectedDays);
  const NumberOfPreviousTimeEntryMonthsAllowed = useSelector(state => state.config.configuration.NumberOfPreviousTimeEntryMonthsAllowed);

  const [isOpen, setIsOpen] = useState(true);
  const [lab] = useState(props.lab);

  const handleInputChange = async (change, finishedLoading, resetValueCallback) => {
    if (change.newValue == change.previousValue) {
      finishedLoading();
      return;
    }

    const dateOnly = dayjs(change.date).format('YYYY-MM-DD');

    try {
      await dispatch(
        labEntryActions.upsertLabTimeEntry(
          userId,
          dateOnly,
          change.entry.hours,
          lab.projectNumber,
          change.entry.procedureId,
          change.entry.requestId,
          lab.requestNumber
        )
      );


      var updateCompletedMonth = dayjs(dateOnly).month() === dayjs(selectedDays[0]).month();
      await dispatch(
        projectEntryActions.updateCompletedWeeks(change.newValue - change.previousValue, updateCompletedMonth)
      );
    } catch (e) {
      handleFetchError(e, getErrMsg(e));
      resetValueCallback();
    } finally {
      finishedLoading();
    }
  }

  const getErrMsg = (e) => {
    // If error has a Message property (from ValidationErrorResponse), use it directly
    if (e && e.Message) {
      return { title: 'Error: Project Entries', message: e.Message };
    }
    if (e.Message === errorSavingEntryHours.message) {
      return errorSavingEntryHours;
    }
    return errorSavingEntry;
  }

  const getTimeEntry = (procedure, date) => {
    const entry = procedure.timeEntries.find(entry => dayjs(entry.timeEntry.entryDate).format('MM/DD/YYYY 00:00:00') === dayjs(date).format('MM/DD/YYYY 00:00:00'))
    const timeEntry = {
      ...entry?.timeEntry,
      hours: entry ? entry.timeEntry.hours : 0,
      procedureId: procedure.procedureId, //procedureIdLegacy
      requestId: lab.requestId, //requestIdLegacy
    };

    return timeEntry;
  }

  const lrsPrefix = lab.isNewLR ? 'GRS LR' : 'LR';
  const headerTitle = `${lrsPrefix}-${lab.requestNumber}`;

  return (
    <>
      <HeaderRow onClick={() => setIsOpen(!isOpen)}>
        <StyledArrowIcon icon="angle-right" isOpen={isOpen} />
        <span>{headerTitle}</span>
      </HeaderRow>

      {
        // use procedures from passed from props so react can re-render when procedure is added
        props.procedures?.sort((a, b) => {
          if (a.procedureAlphaId < b.procedureAlphaId) return -1;
          if (a.procedureAlphaId > b.procedureAlphaId) return 1;
          return 0;
        }).map((procedure, procedureIndex) => {

          const canDeleteProcedure = procedure.timeEntries.every(
            t => t.timeEntry.hours === 0
          );

          const url = procedure.timeEntries[0]?.requestId ?
            procedure.procedureAlphaId ?
              `${GrsEnvironmentUrl()}/requests/labrequest/${procedure.timeEntries[0]?.requestId}/procedure/${procedure.procedureId}`
              : `${GrsEnvironmentUrl()}/requests/labrequest/${procedure.timeEntries[0]?.requestId}`
            : null;
          return (
            <ProjectEntryRow key={procedureIndex} align='center' active={isOpen} style={{ marginRight: '3px' }}>
              <Grid.Col lg={8} md={7} xs={12}>
                <Grid align="center">
                  <Grid.Col span={12} lg={5}>
                    <Grid>
                      <Grid.Col span={5}>
                        <a href={url} target="_blank" rel="noreferrer">{`P-${procedure.procedureAlphaId ?? procedure.procedureNumber}`}</a>
                      </Grid.Col>
                      <Grid.Col span={7}>
                        {procedure.procedureTitle}
                      </Grid.Col>
                    </Grid>
                  </Grid.Col>
                  <Grid.Col span={12} lg={5} data-toggle="tooltip" title={procedure.procedureExplanation}>
                    {procedure.procedureExplanation}
                  </Grid.Col>
                  <Grid.Col span={12} lg={2}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <FillWeekControl
                        type={SupportedSystems.LRS}
                        projectData={{ requestNumber: lab.requestNumber, requestId: lab.requestId, projectNumber: lab.projectNumber, isNewLR: lab.isNewLR, procedure }}
                      />
                    </div>
                  </Grid.Col>
                </Grid>
              </Grid.Col>
              <Grid.Col lg={4} md={5} sm={8} xs={12}>
                <Grid sm={{ justify: 'space-around' }} align="center">
                  {selectedDays.map((date) => {
                    const timeEntry = getTimeEntry(procedure, date)
                    return <NewHourInput id={date} key={`procedure-${procedureIndex}-key-${date}-${timeEntry.hours}`} entry={timeEntry} editable={isTimeEntryDateValid(date, NumberOfPreviousTimeEntryMonthsAllowed)} date={dayjs(date).format('MM/DD/YYYY 00:00:00')} handleInputChange={handleInputChange} />
                  })}
                  {canDeleteProcedure && (<DeleteButton labRequestId={lab.requestId} labProcedureId={procedure.procedureId} handleDelete={props.handleDeleteLab} />)}
                </Grid>
              </Grid.Col>
            </ProjectEntryRow>
          )
        })
      }
    </>
  )
}

LabSection.propTypes = {
  lab: PropTypes.object,
  procedures: PropTypes.arrayOf(PropTypes.object),
  handleDeleteLab: PropTypes.func.isRequired,
}

export default LabSection;
